import React, { useState, useEffect, useRef } from 'react';

/**
 * WithProgression - Progressive image loading using native browser features
 *
 * Implements progressive image loading as described in the second article:
 * - Uses IntersectionObserver (native API) for lazy loading detection
 * - Shows low-quality placeholder first, then transitions to high-quality
 * - Leverages native browser image loading capabilities
 * - Supports srcset on both images for responsive optimization
 *
 * @component
 * @param {Object} props - Component props
 * @param {string} [props.lowQualitySrcSet] - Optional srcset for low-quality image
 * @param {string} [props.highQualitySrcSet] - Optional srcset for high-quality image
 * @param {string} [props.sizes] - Optional sizes attribute for responsive images
 * @param {string} props.alt - Alt text for the image (accessibility)
 * @param {Object} [props.style] - CSS styles to apply to the image container
 * @param {string} [props.className] - Additional CSS class names for the container
 * @param {Function} [props.onLoadComplete] - Callback function called when the high-quality image finishes loading
 * @param {string} [props.loading] - Loading strategy: 'lazy' (default) or 'eager'
 *
 * @example
 * <WithProgression
 *   lowQualitySrcSet="/landing/hero-vacation-400px.png 400w"
 *   highQualitySrcSet="/landing/hero-vacation-700px.png 700w"
 *   sizes="100vw"
 *   alt="Saving for vacation made easy"
 *   style={{ width: '100%', height: '100%', objectFit: 'cover' }}
 * />
 */
const WithProgression = ({
  lowQualitySrcSet,
  highQualitySrcSet,
  sizes,
  alt,
  style,
  className,
  onLoadComplete,
  loading = 'lazy'
}) => {
  const [highQualityLoaded, setHighQualityLoaded] = useState(false);
  const [highQualityRendered, setHighQualityRendered] = useState(false);
  const [isIntersecting, setIsIntersecting] = useState(false);
  const [lowQualityLoaded, setLowQualityLoaded] = useState(false);
  const [shouldLoadHighQuality, setShouldLoadHighQuality] = useState(false);
  const containerRef = useRef(null);
  const highQualityImgRef = useRef(null);

  // Use IntersectionObserver (native API) to detect when image enters viewport
  useEffect(() => {
    if (!containerRef.current) return;

    // If loading is eager, start loading immediately
    if (loading === 'eager') {
      setIsIntersecting(true);
      if (process.env.NODE_ENV === 'development') {
        console.log('[WithProgression] Eager loading - starting immediately');
      }
      return;
    }

    // Use native IntersectionObserver API for lazy loading
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            const startTime = performance.now();
            setIsIntersecting(true);
            if (process.env.NODE_ENV === 'development') {
              console.log(`[WithProgression] Image entered viewport after ${startTime.toFixed(2)}ms`);
            }
            observer.disconnect(); // Stop observing once visible
          }
        });
      },
      {
        rootMargin: '50px' // Start loading 50px before entering viewport
      }
    );

    const observeStartTime = performance.now();
    observer.observe(containerRef.current);
    if (process.env.NODE_ENV === 'development') {
      console.log(`[WithProgression] Started observing at ${observeStartTime.toFixed(2)}ms`);
    }

    return () => {
      observer.disconnect();
    };
  }, [loading]);

  // Wait for low quality to load before starting high quality load
  useEffect(() => {
    // Only start loading high quality AFTER low quality has loaded
    // This ensures the low quality image loads first, then high quality
    if (isIntersecting && lowQualityLoaded && !shouldLoadHighQuality) {
      // Small delay to ensure low quality is fully displayed and rendered
      const timer = setTimeout(() => {
        setShouldLoadHighQuality(true);
      }, 300);
      return () => clearTimeout(timer);
    }
  }, [isIntersecting, lowQualityLoaded, shouldLoadHighQuality]);

  // Debug: Log when images should load (remove in production)
  useEffect(() => {
    if (process.env.NODE_ENV === 'development') {
      console.log('[WithProgression]', {
        isIntersecting,
        lowQualityLoaded,
        shouldLoadHighQuality,
      });
    }
  }, [isIntersecting, lowQualityLoaded, shouldLoadHighQuality]);

  // Track when low-quality image loads
  const handleLowQualityLoad = () => {
    setLowQualityLoaded(true);
  };

  // Track when high-quality image loads and is rendered
  const handleHighQualityLoad = () => {
    setHighQualityLoaded(true);
    // Use requestAnimationFrame to ensure the image is painted before we start fading
    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        // Double RAF ensures the image is fully rendered in the DOM
        setHighQualityRendered(true);
        if (onLoadComplete) onLoadComplete();
      });
    });
  };

  return (
    <div
      ref={containerRef}
      style={{ position: 'relative', ...style }}
      className={className}
    >
      {/* Low quality image - loads immediately when visible, fades out only after high-quality is fully rendered */}
      {isIntersecting && (
        <img
          key="low-quality" // Key to prevent React from reusing the element
          srcSet={lowQualitySrcSet}
          sizes={sizes}
          alt={alt}
          onLoad={handleLowQualityLoad}
          style={{
            ...style,
            position: 'absolute',
            top: 0,
            left: 0,
            opacity: highQualityRendered ? 0 : 1,
            transition: highQualityRendered ? 'opacity 0.5s ease-in-out' : 'none',
            zIndex: 10,
          }}
          loading={loading}
          decoding="async"
          fetchPriority="high" // Prioritize low quality image
        />
      )}

      {/* High quality image - only starts loading after low quality has loaded */}
      {shouldLoadHighQuality && (
        <img
          ref={highQualityImgRef}
          key="high-quality" // Key to prevent React from reusing the element
          srcSet={highQualitySrcSet}
          sizes={sizes}
          alt={alt}
          onLoad={handleHighQualityLoad}
          style={{
            ...style,
            position: 'absolute',
            top: 0,
            left: 0,
            opacity: highQualityLoaded ? 1 : 0,
            transition: highQualityLoaded ? 'opacity 0.3s ease-in-out' : 'none',
            zIndex: 9,
          }}
          loading={loading}
          decoding="async"
          fetchPriority="low" // Lower priority for high quality
        />
      )}
    </div>
  );
};

export default WithProgression;
